import { useRef } from "react";
import { Label } from "../../../../components/tailwind/label";
import { Input } from "../../../../components/tailwind/input";
import { Textarea } from "../../../../components/tailwind/textarea";
import { Button } from "../../../../components/tailwind/button";
import { TemplateMessageConfig } from "./TemplateMessageConfig";
import { WorkflowMediaField } from "./WorkflowMediaField";
import { KeyValueField } from "./KeyValueField";
import { ConditionCatalogSelect } from "./ConditionNodeFields";
import {
  inspectorInputClass,
  inspectorTextareaClass,
  inspectorTextareaMonoClass,
  inspectorSelectClass
} from "./RightInspectorStyles";
import {
  getDefaultValue,
  isSchemaFieldVisible,
  schemaToFields
} from "../../utils/workflowSchemaUtils";
import { isMapTransitionPalette } from "../../utils/workflowPaletteConstants";
import {
  MESSAGE_VARIABLES,
  applyMediaUploadMeta,
  fieldSupportsVariables,
  getFriendlyFieldLabel,
  isMediaUploadField,
  normalizeButtonOptions,
  normalizeListItems,
  resolveInspectorFieldValue,
  resolveWorkflowMediaType,
  shouldHideNestedField,
  usesWorkflowPicker,
  resolveConfigMessageType
} from "../../utils/workflowInspectorUx";

const normalizeArrayFieldItems = (fieldName, items) => {
  if (fieldName === "options") return normalizeButtonOptions(items);
  if (fieldName === "items") return normalizeListItems(items);
  return items;
};

function ActionCatalogSelect({ value, onChange, actions }) {
  const grouped = actions.reduce((groups, action) => {
    if (!groups[action.category]) groups[action.category] = [];
    groups[action.category].push(action);
    return groups;
  }, {});

  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className={inspectorSelectClass}
    >
      <option value="">Select...</option>
      {Object.entries(grouped).map(([category, categoryActions]) => (
        <optgroup key={category} label={category}>
          {categoryActions.map((action) => (
            <option key={action.type} value={action.type}>
              {action.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function WorkflowSelect({ value, onChange, workflows, loading, excludeWorkflowId }) {
  const options = (workflows || []).filter((workflow) => workflow.workflowId !== excludeWorkflowId);

  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={loading}
      className={inspectorSelectClass}
    >
      <option value="">{loading ? "Loading workflows..." : "Select a workflow..."}</option>
      {options.map((workflow) => (
        <option key={workflow.workflowId} value={workflow.workflowId}>
          {workflow.name || workflow.workflowId}
        </option>
      ))}
    </select>
  );
}

function SchemaSelect({ field, value, onChange, actions, conditions }) {
  if (field.dataSource === "ACTION_CATALOG") {
    return <ActionCatalogSelect value={value} onChange={onChange} actions={actions} />;
  }
  if (field.dataSource === "CONDITION_CATALOG") {
    return <ConditionCatalogSelect value={value} onChange={onChange} conditions={conditions} />;
  }

  return (
    <select
      value={value ?? field.default ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={inspectorSelectClass}
    >
      <option value="">Select...</option>
      {(field.options || []).map((option) => {
        if (typeof option === "string") {
          return (
            <option key={option} value={option}>
              {option}
            </option>
          );
        }
        return (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        );
      })}
    </select>
  );
}

function TemplateTextarea({ value, onChange, placeholder }) {
  const inputRef = useRef(null);

  const insertVariable = (token) => {
    const el = inputRef.current;
    const current = value ?? "";
    if (!el) {
      onChange(current + token);
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.substring(0, start) + token + current.substring(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    }, 0);
  };

  return (
    <div className="space-y-2">
      <Textarea
        ref={inputRef}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Write the message your customer will see..."}
        className={inspectorTextareaClass}
      />
      <div className="flex flex-wrap gap-1.5">
        {MESSAGE_VARIABLES.map((variable) => (
          <button
            key={variable.token}
            type="button"
            onClick={() => insertVariable(variable.token)}
            className="text-[10px] bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 text-[#0F6E56] hover:bg-[#E1F5EE] hover:border-[#9FE1CB] px-2 py-1 rounded transition-colors font-medium"
          >
            {variable.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ConfigField({
  field,
  value,
  onChange,
  config,
  catalog,
  paletteType,
  parentFieldName,
  visibilityContext,
  workflows,
  workflowsLoading,
  currentWorkflowId,
  mediaFilename,
  mediaPendingFile,
  saveSignal,
  uploadSilent,
  nodeId,
  onPendingMediaChange,
  onRegisterPendingUploadNode,
  showErrors
}) {
  const { actions, conditions } = catalog;
  const displayValue = resolveInspectorFieldValue(field, value);

  if (field.name === "message" && paletteType && (paletteType.includes("template") || value?.type === "template")) {
    return (
      <TemplateMessageConfig
        value={value}
        onChange={onChange}
        paletteType={paletteType}
        nodeId={nodeId}
        saveSignal={saveSignal}
        uploadSilent={uploadSilent}
        onPendingMediaChange={onPendingMediaChange}
        onRegisterPendingUploadNode={onRegisterPendingUploadNode}
      />
    );
  }

  if (!isSchemaFieldVisible(field, visibilityContext ?? config)) {
    return null;
  }

  if (field.name === "_note" || field.type === "info") {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
        {displayValue || field.text || ""}
      </p>
    );
  }

  if (field.name === "workflowId" && usesWorkflowPicker(paletteType)) {
    return (
      <WorkflowSelect
        value={value}
        workflows={workflows}
        loading={workflowsLoading}
        excludeWorkflowId={currentWorkflowId}
        onChange={onChange}
      />
    );
  }

  if (field.type === "info") {
    return <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded p-2">{field.text}</p>;
  }

  if (isMediaUploadField(field.name, config, parentFieldName, paletteType)) {
    return (
      <WorkflowMediaField
        value={displayValue ?? ""}
        filename={mediaFilename ?? ""}
        pendingFileProp={mediaPendingFile ?? null}
        mediaType={resolveWorkflowMediaType(field.name, config, parentFieldName, paletteType)}
        nodeId={nodeId}
        saveSignal={saveSignal}
        uploadSilent={uploadSilent}
        onPendingChange={onPendingMediaChange}
        onRegisterPendingUploadNode={onRegisterPendingUploadNode}
        onChange={(nextValue, meta) => onChange(nextValue, meta)}
      />
    );
  }

  switch (field.type) {
    case "textarea":
      if (fieldSupportsVariables(field)) {
        return <TemplateTextarea value={displayValue} onChange={onChange} placeholder={field.placeholder} />;
      }
      return (
        <Textarea
          value={displayValue ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={inspectorTextareaClass}
        />
      );

    case "json":
      return (
        <Textarea
          value={typeof value === "object" ? JSON.stringify(value, null, 2) : (value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={inspectorTextareaMonoClass}
        />
      );

    case "expression":
    case "text":
      return (
        <Input
          value={displayValue ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || field.hint}
          className={inspectorInputClass}
        />
      );

    case "select":
      return <SchemaSelect field={field} value={value} onChange={onChange} actions={actions} conditions={conditions} />;

    case "number":
      return (
        <Input
          type="number"
          value={value ?? field.default ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          min={field.min}
          max={field.max}
          className={inspectorInputClass}
        />
      );

    case "boolean":
    case "checkbox":
      return (
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span>{field.checkboxLabel || field.label || "Enabled"}</span>
        </label>
      );

    case "keyvalue":
      return <KeyValueField value={value} onChange={onChange} field={field} showErrors={showErrors} />;

    case "array": {
      const items = displayValue;
      const itemFields = schemaToFields(field.itemSchema).filter(
        (itemField) => !shouldHideNestedField(itemField.name, field.name, paletteType)
      );
      const hasEmptyOption = (field.name === "options" || field.name === "items") && items.some(item => !String(item.label || "").trim());
      const atMaxItems = (field.maxItems != null && items.length >= field.maxItems) || hasEmptyOption;

      const addItem = () => {
        if (atMaxItems) return;
        const emptyItem = itemFields.reduce(
          (acc, itemField) => ({ ...acc, [itemField.name]: getDefaultValue(itemField) }),
          {}
        );
        const nextItems = [...items, emptyItem];
        onChange(normalizeArrayFieldItems(field.name, nextItems));
      };

      const updateItems = (nextItems) => {
        onChange(normalizeArrayFieldItems(field.name, nextItems));
      };

      const addLabel = field.name === "options" ? "Add button" : "Add option";
      const removeLabel = field.name === "options" ? "Remove button" : "Remove option";
      const warningLabel = field.name === "options"
        ? "Please fill in existing buttons before adding new ones."
        : "Please fill in existing menu options before adding new ones.";
      const maxLabel = field.name === "options"
        ? `Maximum ${field.maxItems} buttons.`
        : `Maximum ${field.maxItems} menu options.`;

      return (
        <div className="space-y-3">
          {items.map((item, itemIndex) => (
            <div key={`${field.name}-item-${itemIndex}`} className="space-y-2 rounded border border-gray-200 dark:border-slate-800 p-3">
              {itemFields.map((itemField) => (
                <div className="space-y-1" key={`${field.name}-${itemIndex}-${itemField.name}`}>
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    {getFriendlyFieldLabel(itemField, paletteType)}
                  </Label>
                  <ConfigField
                    field={itemField}
                    value={item[itemField.name] ?? getDefaultValue(itemField)}
                    onChange={(nextValue) => {
                      const nextItems = [...items];
                      nextItems[itemIndex] = { ...nextItems[itemIndex], [itemField.name]: nextValue };
                      updateItems(nextItems);
                    }}
                    config={config}
                    catalog={catalog}
                    paletteType={paletteType}
                    parentFieldName={field.name}
                    mediaFilename={item.filename}
                    mediaPendingFile={item.pendingFile}
                    saveSignal={saveSignal}
                    uploadSilent={uploadSilent}
                    nodeId={nodeId}
                    onPendingMediaChange={onPendingMediaChange}
                    onRegisterPendingUploadNode={onRegisterPendingUploadNode}
                    showErrors={showErrors}
                  />
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                className="h-7 text-xs text-red-600 dark:text-red-400"
                onClick={() => updateItems(items.filter((_, idx) => idx !== itemIndex))}
              >
                {removeLabel}
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" className="w-full h-8 text-xs" onClick={addItem} disabled={atMaxItems}>
            {addLabel}
          </Button>
          {hasEmptyOption && (
            <p className="text-[10px] text-red-500">{warningLabel}</p>
          )}
          {atMaxItems && field.maxItems != null && !hasEmptyOption && (
            <p className="text-[10px] text-gray-400">{maxLabel}</p>
          )}
        </div>
      );
    }

    case "object": {
      const objectValue = value || {};
      const subFields = schemaToFields(field.fields).filter(
        (subField) => !shouldHideNestedField(subField.name, field.name, paletteType)
      );
      return (
        <div className="space-y-3 rounded-md border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-950/50 p-3">
          {subFields.map((subField) => (
            <div className="space-y-1" key={`${field.name}-${subField.name}`}>
              {!isMediaUploadField(subField.name, config, field.name, paletteType) && (
                <>
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    {getFriendlyFieldLabel(subField, paletteType)}
                  </Label>
                  {subField.hint && <p className="text-xs text-gray-400">{subField.hint}</p>}
                </>
              )}
              <ConfigField
                field={subField}
                value={objectValue[subField.name]}
                onChange={(nextValue, meta) => {
                  let nextObject = { ...objectValue, [subField.name]: nextValue };
                  nextObject = applyMediaUploadMeta(nextObject, subField.name, meta);
                  if (field.name === "message" && isMapTransitionPalette(paletteType, config)) {
                    const isButtons = /\.buttons(\.|$)/.test(paletteType) ||
                      (paletteType === "whatsapp.await" && (nextObject.type === "buttons" || config?.message?.type === "buttons"));
                    const isList = /\.list(\.|$)/.test(paletteType) ||
                      (paletteType === "whatsapp.await" && (nextObject.type === "list" || config?.message?.type === "list"));

                    if (isButtons && subField.name === "options") {
                      nextObject.options = normalizeButtonOptions(nextObject.options);
                    }
                    if (
                      isList &&
                      subField.name === "items" &&
                      Array.isArray(nextObject.items)
                    ) {
                      nextObject.items = normalizeListItems(nextObject.items);
                    }
                  }
                  onChange(nextObject);
                }}
                config={config}
                catalog={catalog}
                paletteType={paletteType}
                parentFieldName={field.name}
                mediaFilename={objectValue.filename}
                mediaPendingFile={objectValue.pendingFile}
                saveSignal={saveSignal}
                uploadSilent={uploadSilent}
                nodeId={nodeId}
                onPendingMediaChange={onPendingMediaChange}
                onRegisterPendingUploadNode={onRegisterPendingUploadNode}
                showErrors={showErrors}
              />
            </div>
          ))}
        </div>
      );
    }

    case "dynamic": {
      if (field.dataSource === "ACTION_CATALOG_SCHEMA") {
        const selectedAction = actions.find((action) => action.type === config.action);
        if (!selectedAction) {
          return <p className="text-xs text-gray-500 dark:text-gray-400">Select an action type first.</p>;
        }
        const inputFields = schemaToFields(selectedAction.inputSchema);
        const inputValue = value || {};
        return (
          <div className="space-y-3 rounded-md border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-950/50 p-3">
            {inputFields.map((inputField) => (
              <div className="space-y-1" key={`${field.name}-${inputField.name}`}>
                <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                  {getFriendlyFieldLabel(inputField, paletteType)}
                </Label>
                <ConfigField
                  field={inputField}
                  value={inputValue[inputField.name] ?? getDefaultValue(inputField)}
                  onChange={(nextValue, meta) => {
                    let nextInput = { ...inputValue, [inputField.name]: nextValue };
                    nextInput = applyMediaUploadMeta(nextInput, inputField.name, meta);
                    onChange(nextInput);
                  }}
                  config={config}
                  catalog={catalog}
                  paletteType={paletteType}
                  visibilityContext={inputValue}
                  mediaFilename={inputValue.filename}
                  mediaPendingFile={inputValue.pendingFile}
                  saveSignal={saveSignal}
                  uploadSilent={uploadSilent}
                  nodeId={nodeId}
                  onPendingMediaChange={onPendingMediaChange}
                  onRegisterPendingUploadNode={onRegisterPendingUploadNode}
                  showErrors={showErrors}
                />
              </div>
            ))}
          </div>
        );
      }

      if (field.variants) {
        const dependsOnValue =
          config[field.dependsOn] ??
          (field.dependsOn === "type" ? resolveConfigMessageType(config, paletteType) : undefined);
        const variantSchema = field.variants[dependsOnValue];
        if (!variantSchema) {
          return <p className="text-xs text-gray-500 dark:text-gray-400">Select {field.dependsOn} to configure.</p>;
        }
        const variantFields = schemaToFields(variantSchema).filter(
          (variantField) => !shouldHideNestedField(variantField.name, field.name, paletteType)
        );
        const variantValue = value || {};
        return (
          <div className="space-y-3 rounded-md border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-950/50 p-3">
            {variantFields.map((variantField) => (
              <div className="space-y-1" key={`${field.name}-${variantField.name}`}>
                {!isMediaUploadField(variantField.name, config, field.name, paletteType) && (
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    {getFriendlyFieldLabel(variantField, paletteType)}
                  </Label>
                )}
                <ConfigField
                  field={variantField}
                  value={variantValue[variantField.name] ?? getDefaultValue(variantField)}
                  onChange={(nextValue, meta) => {
                    let nextObject = { ...variantValue, [variantField.name]: nextValue };
                    nextObject = applyMediaUploadMeta(nextObject, variantField.name, meta);
                    onChange(nextObject);
                  }}
                  config={config}
                  catalog={catalog}
                  paletteType={paletteType}
                  parentFieldName={field.name}
                  mediaFilename={variantValue.filename}
                  mediaPendingFile={variantValue.pendingFile}
                  saveSignal={saveSignal}
                  uploadSilent={uploadSilent}
                  nodeId={nodeId}
                  onPendingMediaChange={onPendingMediaChange}
                  onRegisterPendingUploadNode={onRegisterPendingUploadNode}
                  showErrors={showErrors}
                />
              </div>
            ))}
          </div>
        );
      }
      break;
    }

    default:
      break;
  }

  return (
    <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded p-2">
      Unsupported field type &quot;{field.type}&quot; for &quot;{field.name}&quot;.
    </p>
  );
}
